/**
 * Active workspace — module-level state shared between the React tree
 * (via useAppContext) and the API client (lib/api.ts).
 *
 * Why module-level instead of context? api.ts is plain TypeScript and
 * shouldn't import React. Boot flow:
 *
 *   1. AppContextProvider mounts.
 *   2. It fetches /api/workspaces/me, gets the active workspace.
 *   3. Calls setActiveWorkspaceId(id) — fires once at boot.
 *   4. Now every api.ts helper that calls workspaceUrl() picks up the
 *      id and prefixes URLs with /api/workspaces/<id>/.
 *
 * Single source of truth: every workspace-scoped URL is built through
 * `workspaceUrl()` in api.ts, which reads from this module. No other
 * file should poke at activeWorkspaceId directly — they go through
 * the React context (`useAppContext`) for read access in components.
 */

let activeWorkspaceId: string | null = null

/** Set by AppContextProvider once the boot fetch of `/me` resolves.
 * Idempotent — safe to call multiple times with the same value. */
export function setActiveWorkspaceId(id: string): void {
  activeWorkspaceId = id
}

/** Read the active workspace id, or null if not yet resolved. */
export function getActiveWorkspaceId(): string | null {
  return activeWorkspaceId
}

/** Read the active workspace id; throw if not yet set. Used by api.ts
 * URL builders so a misordered call (API request before boot resolved
 * /me) surfaces immediately with a clear message rather than a 404
 * from the server seeing the literal string "null" in the path. */
export function requireActiveWorkspaceId(): string {
  if (!activeWorkspaceId) {
    throw new Error(
      '[workspace] activeWorkspaceId is not set — API call attempted before AppContextProvider resolved /api/workspaces/me. Either wait for the context to load or use a flat (non-workspace-scoped) endpoint.',
    )
  }
  return activeWorkspaceId
}
